/**
 * Authentication context and hook
 * Manages user session and authentication state for UserPool admin
 */

import { UserPoolClient } from "@eoln/sdk-userpool";
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { clearSession, getSession, hasSession, setSession } from "@/lib/session";

const USERPOOL_API_URL = import.meta.env.VITE_USERPOOL_API_URL || "https://userpool-api.mono-dev.eoln.eu/graphql";

interface AuthContextValue {
  isAuthenticated: boolean;
  apiKey: string | null;
  login: (apiKey: string) => Promise<void>;
  logout: () => void;
  client: UserPoolClient | null;
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(hasSession());
  const [apiKey, setApiKey] = useState<string | null>(getSession());

  const client = useMemo(
    () =>
      apiKey
        ? new UserPoolClient({
            endpoint: USERPOOL_API_URL,
            apiKey,
          })
        : null,
    [apiKey],
  );

  const login = useCallback(async (newApiKey: string) => {
    // Validate API key format: sk_ + 64 hex characters
    const apiKeyPattern = /^sk_[0-9a-f]{64}$/;
    if (!apiKeyPattern.test(newApiKey)) {
      throw new Error("Invalid API key format. Expected: sk_ followed by 64 hex characters");
    }

    // Validate key with backend by calling listUsers (requires userpool:read scope)
    const tempClient = new UserPoolClient({
      endpoint: USERPOOL_API_URL,
      apiKey: newApiKey,
    });

    try {
      await tempClient.listUsers({ pagination: { limit: 1 } });
    } catch (error) {
      if (error instanceof Error) {
        if (error.message.includes("UNAUTHENTICATED")) {
          throw new Error("API key is not valid or has been revoked");
        }
        if (error.message.includes("FORBIDDEN")) {
          throw new Error("API key does not have userpool:read or userpool:admin scope");
        }
      }
      throw error;
    }

    // Store in session
    setSession(newApiKey);
    setApiKey(newApiKey);
    setIsAuthenticated(true);
  }, []);

  const logout = useCallback(() => {
    clearSession();
    setApiKey(null);
    setIsAuthenticated(false);
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, apiKey, login, logout, client }}>{children}</AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
